import {
  useMemo,
  useState,
} from 'react';
import type {
  ChangeEvent,
} from 'react';
import type {
  CurrencyEntity,
} from '@dinosave/scraping/types';
import {
  CURRENCIES,
} from '@/constants';
import Button from '@/ui/components/Button';
import Input from '@/ui/components/Input';
import Text from '@/ui/components/Text';
import {
  SearchIcon,
} from '@/ui/icons';

import styles from './CurrencyList.module.css';

type CurrencyListProps = {
  value?: CurrencyEntity['code'];
  onChange: (currency: CurrencyEntity) => void;
}

const CurrencyList = (props: CurrencyListProps) => {
  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    onChange,
  } = props;

  // ##########################################################################
  // STATE
  // ##########################################################################

  const [search, setSearch] = useState('');

  // ##########################################################################
  // MEMOIZATION
  // ##########################################################################

  const filteredCurrencies = useMemo(() => (
    CURRENCIES?.filter((currency: CurrencyEntity) => (
      currency.name.toLowerCase().includes(search.toLowerCase()) ||
      currency.code.toLowerCase().includes(search.toLowerCase())
    )) || []
  ), [search]);

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const handleChange = (currency: CurrencyEntity) => () => {
    onChange(currency);
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <div className={styles.currencyList}>
      <div className={styles.search}>
        <Input
          value={search}
          onChange={handleSearchChange}
          placeholder="Search..."
          icon={SearchIcon}
          autoComplete="off"
          autoFocus
        />
      </div>

      <div className={styles.list}>
        {filteredCurrencies.map((currency: CurrencyEntity) => (
          <div
            key={currency.code}
            className={styles.currency}
          >
            <Button
              kind="ghost"
              color="secondary"
              onClick={handleChange(currency)}
            >
              <img src={currency.flag} />

              <div className={styles.name}>
                <Text
                  kind="control"
                  size="small"
                >
                  {currency.name}
                </Text>
              </div>

              {!!currency.symbols.length && (
                <Text
                  kind="control"
                  size="small"
                >
                  {currency.symbols[0]}
                </Text>
              )}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CurrencyList;
